// client/src/components/services_tester/MonitoringTab.jsx
import React from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Stack,
  Divider,
  CircularProgress
} from '@mui/material';
import {
  VpnKey,
  Visibility,
  ListAlt,
  AdminPanelSettings,
  People,
  Security,
  Hub,
  AccountTree,
  Dns,
  Build,
  Info
} from '@mui/icons-material';
import CompactInfrastructureCard from '../common/CompactInfrastructureCard';
import ServiceStatus from '../common/ServiceStatus';
import { DIRECT_SERVICES } from './common/constants';

export default function MonitoringTab({
  networkDiagnostics,
  isAuthenticated,
  rabbitmqStatus,
  tarantoolStatus,
  mongodbStatus,
  featureTreeStatus,
  kratosStatus,
  infrastructureLoading,
  handleCheckAuth,
  handleRabbitMQHealth,
  handleRabbitMQDetails,
  handleRabbitMQTest,
  handleKratosHealth,
  handleKratosAdminHealth,
  handleKratosUsers,
  handleKratosDetails,
  handleKratosDetailed,
  handleFeatureTreeCheck,
  handleTarantoolHealth,
  handleMongoDBHealth,
  handleTarantoolDetailed,
  handleMongoDBDetailed,
  handleTestImageLangCodec
}) {
  return (
    <Box sx={{
      p: 1.5,
      flex: 1,
      overflow: 'auto',
      minHeight: 0
    }}>
      <Typography variant="h6" gutterBottom sx={{ flexShrink: 0 }}>
        Статистика и мониторинг
      </Typography>

      {/* Infrastructure Status Grid */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <CompactInfrastructureCard
            title="RabbitMQ"
            icon={<Hub />}
            status={rabbitmqStatus}
            onCheck={handleRabbitMQHealth}
            onDetailed={handleRabbitMQDetails}
            loading={infrastructureLoading.rabbitmq}
            size="small"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <CompactInfrastructureCard
            title="Kratos Public"
            icon={<Security />}
            status={kratosStatus.public}
            onCheck={handleKratosHealth}
            loading={infrastructureLoading.kratos}
            size="small"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <CompactInfrastructureCard
            title="Kratos Admin"
            icon={<AdminPanelSettings />}
            status={kratosStatus.admin}
            onCheck={handleKratosAdminHealth}
            loading={infrastructureLoading.kratos_admin}
            size="small"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <CompactInfrastructureCard
            title="Feature Tree"
            icon={<AccountTree />}
            status={featureTreeStatus}
            onCheck={handleFeatureTreeCheck}
            loading={infrastructureLoading.featureTree}
            size="small"
          />
        </Grid>
      </Grid>

      <Divider sx={{ my: 3 }} />

      {/* Дополнительные тесты */}
      <Typography variant="h6" gutterBottom>
        Дополнительные тесты
      </Typography>
      <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap sx={{ mb: 3 }}>
        <Button
          variant="outlined"
          startIcon={<AccountTree />}
          onClick={handleTestImageLangCodec}
          disabled={infrastructureLoading.featureTree}
          size="small"
        >
          Проверить кодек ImageLang
        </Button>

        <Button
          variant="outlined"
          startIcon={<VpnKey />}
          onClick={handleCheckAuth}
          size="small"
        >
          Проверить сессию
        </Button>

        <Button
          variant="outlined"
          startIcon={infrastructureLoading.rabbitmq ? <CircularProgress size={16} /> : <Visibility />}
          onClick={handleRabbitMQDetails}
          disabled={infrastructureLoading.rabbitmq || !rabbitmqStatus}
          size="small"
        >
          Детали RabbitMQ
        </Button>

        <Button
          variant="outlined"
          startIcon={infrastructureLoading.rabbitmq ? <CircularProgress size={16} /> : <ListAlt />}
          onClick={handleRabbitMQTest}
          disabled={infrastructureLoading.rabbitmq}
          size="small"
        >
          Тест публикации
        </Button>

        <Button
          variant="outlined"
          startIcon={infrastructureLoading.kratos ? <CircularProgress size={16} /> : <AdminPanelSettings />}
          onClick={handleKratosDetails}
          disabled={infrastructureLoading.kratos || !kratosStatus.public}
          size="small"
        >
          Детали Kratos
        </Button>

        <Button
          variant="outlined"
          startIcon={infrastructureLoading.kratos ? <CircularProgress size={16} /> : <People />}
          onClick={handleKratosUsers}
          disabled={infrastructureLoading.kratos}
          size="small"
        >
          Статистика пользователей
        </Button>

        <Button
          variant="outlined"
          startIcon={infrastructureLoading.kratos ? <CircularProgress size={16} /> : <Visibility />}
          onClick={handleKratosDetailed}
          disabled={infrastructureLoading.kratos}
          size="small"
        >
          Детальная диагностика Kratos
        </Button>

        <Button
          variant="outlined"
          startIcon={infrastructureLoading.tarantool ? <CircularProgress size={16} /> : <Build />}
          onClick={handleTarantoolDetailed}
          disabled={infrastructureLoading.tarantool}
          size="small"
        >
          Детальная проверка Tarantool
        </Button>

        <Button
          variant="outlined"
          startIcon={infrastructureLoading.mongodb ? <CircularProgress size={16} /> : <Dns />}
          onClick={handleMongoDBDetailed}
          disabled={infrastructureLoading.mongodb}
          size="small"
        >
          Детальная проверка MongoDB
        </Button>
      </Stack>

      {/* Service Status Overview */}
      <Typography variant="h6" gutterBottom>
        Статус сервисов
      </Typography>
      <Grid container spacing={2}>
        {DIRECT_SERVICES.map(service => {
          const diagnostics = networkDiagnostics[service.key] || {};
          return (
            <Grid item xs={12} sm={6} md={4} key={service.key}>
              <Card variant="outlined">
                <CardContent sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="subtitle2" fontWeight="medium">
                      {service.name}
                    </Typography>
                    {service.requiresAuth && (
                      <Security fontSize="small" color={isAuthenticated ? 'success' : 'warning'} />
                    )}
                  </Box>
                  <ServiceStatus status={diagnostics} serviceName="" />
                  {diagnostics.message && (
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                      {diagnostics.message}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
}
